import styled from '@emotion/styled';
import { Spin } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';

const FallbackOverlay = styled.div`
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.15);
`;
const SpinContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
`;

interface FallbackComponentProps {
  children?: React.ReactNode;
  requesting?: boolean; // Whether to show loading state
  overlay?: boolean; // Show full-screen overlay
  selfOverlay?: boolean; // Show overlay relative to parent container
}

/**
 * A flexible loading indicator component with multiple display modes
 * Features:
 * - Minimum display time (500ms) to prevent flickering
 * - Three display modes: inline, full-screen overlay, and container-relative overlay
 * - Smooth transitions between states
 */
export const FallbackComponent: React.FC<FallbackComponentProps> = (props) => {
  const [requesting, setRequesting] = useState(props.requesting);
  const showTime = useRef<number>(0);

  // Effect to handle loading state with minimum display time
  useEffect(() => {
    if (props.requesting) {
      // When loading starts, record the time and show immediately
      setRequesting(props.requesting);
      showTime.current = new Date().getMilliseconds();
    } else {
      // When loading ends, ensure it shows for at least 500ms
      const timeoutId = setTimeout(
        () => {
          setRequesting(props.requesting);
          showTime.current = 0;
        },
        Math.max(500 - new Date().getMilliseconds() + showTime.current, 0)
      );
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [props.requesting]);

  // Determine overlay type
  const isOverlay = props.overlay || props.selfOverlay;

  // Memoized spinner element to prevent unnecessary recreations
  const fallback = useMemo(
    () => (
      <SpinContainer>
        <Spin size="large" />
      </SpinContainer>
    ),
    []
  );

  // Inline mode (no overlay)
  if (requesting && !isOverlay) {
    return fallback;
  }

  // Overlay modes (full-screen or container-relative)
  return (
    <div style={{ position: props.selfOverlay ? 'relative' : 'static' }}>
      {props.children}
      {requesting && isOverlay && (
        <FallbackOverlay data-testid="fallback-overlay">
          {fallback}
        </FallbackOverlay>
      )}
    </div>
  );
};
